/**
 * ProjectTeamCard — compact team-roster sidebar card for the Overview dashboard.
 *
 * Shows the two fixed leadership roles (Project Manager, Solution Architect) with an initials
 * avatar, plus assigned engineers when the free-text `engineers_assigned` column is populated.
 * Read-only — full role editing lives in the Team & Resources tab.
 */
import { Users } from 'lucide-react';

import type { Project } from '@/types';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

/** First + last initial (single initial for one-word names). */
function initials(name: string | null | undefined): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/** Split a free-text engineer list ("A, B; C") into trimmed names. */
function parseEngineers(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Avatar + name + role-label row. */
function MemberRow({
  name,
  role,
}: {
  name: string | null;
  role: string;
}): JSX.Element {
  const hasName = Boolean(name && name.trim());
  return (
    <div className="flex items-center gap-3">
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
        aria-hidden="true"
      >
        {initials(name)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">
          {hasName ? name : 'Unassigned'}
        </p>
        <p className="text-xs text-text-secondary">{role}</p>
      </div>
    </div>
  );
}

export interface ProjectTeamCardProps {
  project: Project;
}

export function ProjectTeamCard({ project }: ProjectTeamCardProps): JSX.Element {
  const engineers = parseEngineers(project.engineers_assigned);

  return (
    <Card>
      <CardHeader>
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <Users className="h-4 w-4 text-primary" aria-hidden="true" />
          Team
        </h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <MemberRow name={project.project_manager} role="Project Manager" />
          <MemberRow name={project.solution_architect} role="Solution Architect" />

          {engineers.length > 0 && (
            <div className="space-y-2 border-t border-outlines pt-3">
              <p className="text-xs text-text-secondary">
                Engineers assigned ({engineers.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {engineers.map((name) => (
                  <div key={name} className="flex items-center gap-1.5">
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                      aria-hidden="true"
                    >
                      {initials(name)}
                    </div>
                    <span className="text-sm text-text-primary">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ProjectTeamCard;
